/**
 * Error handling utilities for React components.
 *
 * Provides consistent error message extraction and classification
 * across the application.
 */

import { isTurnstileErrorCode, TURNSTILE_ERROR_MESSAGE, TURNSTILE_DOCS_PATH } from "../constants/turnstile";
import { getDocsUrl } from "./domainConfig";

export interface ErrorMessageResult {
  message: string;
  isNetworkError: boolean;
  /** True when the error is a Turnstile verification failure (show docs link) */
  isTurnstileError?: boolean;
  /** Docs URL for troubleshooting (only set for Turnstile errors, undefined when brand has no docs) */
  docsUrl?: string;
}

/**
 * Extracts a user-friendly error message from an unknown error.
 * Detects Turnstile errors, network errors, and provides appropriate Polish messages.
 *
 * For Turnstile errors, returns additional fields for rendering docs links.
 * For non-network errors, returns a generic message to avoid leaking internals.
 *
 * @param err - The error to process (can be Error, AxiosError, string, or unknown)
 * @returns Object with message, error classification flags, and optional docs link
 */
export function getErrorMessage(err: unknown): ErrorMessageResult {
  // Detect Axios error with Turnstile-related errorCode
  const axiosData = (err as Record<string, unknown>)?.response as
    | Record<string, unknown>
    | undefined;
  const responseData = axiosData?.data as Record<string, unknown> | undefined;
  if (
    responseData?.errorCode &&
    isTurnstileErrorCode(responseData.errorCode as string)
  ) {
    return {
      message: TURNSTILE_ERROR_MESSAGE,
      isNetworkError: false,
      isTurnstileError: true,
      docsUrl: getDocsUrl(TURNSTILE_DOCS_PATH),
    };
  }

  if (err instanceof Error) {
    const isNetworkError =
      err.message.includes("Network") ||
      err.message.includes("fetch") ||
      err.message.includes("ECONNREFUSED");
    return {
      message: isNetworkError
        ? "Problem z połączeniem. Sprawdź połączenie internetowe."
        : "Wystąpił błąd. Spróbuj ponownie.",
      isNetworkError,
    };
  }
  return { message: "Wystąpił nieoczekiwany błąd.", isNetworkError: false };
}

/**
 * Extracts the message the backend actually sent, so a rejected request can
 * explain itself instead of showing a generic "nie udało się" toast.
 *
 * NestJS ValidationPipe puts validation failures in `message` as a string[];
 * AllExceptionsFilter passes those arrays through untouched. Anything else
 * (network failure, opaque 500) falls back to the caller's own message.
 *
 * @param err - The error to inspect (typically an AxiosError)
 * @param fallback - Message to use when the response carries nothing usable
 * @returns A message safe to show to the user
 */
export function getApiErrorMessage(err: unknown, fallback: string): string {
  const response = (err as { response?: { data?: { message?: unknown } } })
    ?.response;
  const message = response?.data?.message;

  if (typeof message === "string" && message.trim()) {
    return message;
  }

  if (Array.isArray(message)) {
    const joined = message
      .filter((entry): entry is string => typeof entry === "string" && !!entry.trim())
      .join(". ");
    if (joined) {
      return joined;
    }
  }

  return fallback;
}

/**
 * Type guard for HTTP 404 errors from Axios or fetch-style error shapes.
 *
 * Inspects `error.response?.status` (Axios) and `error.status` (alternate shapes)
 * without reaching for `as any`. Returns false for non-object errors.
 *
 * @param error - The error object (typically from a query/mutation hook)
 * @returns True when the error represents an HTTP 404 Not Found response.
 */
export function isNotFoundError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const e = error as {
    response?: { status?: number };
    status?: number;
  };
  return e.response?.status === 404 || e.status === 404;
}
